import { type FC, useContext, useState } from "react";

import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import { useForm } from "react-hook-form";

import { Button, Input, toastError, toastSuccess } from "@alpacahq/ui/old";

import { withAppBasePath } from "~/v2/utils/appBasePath";
import { UserActions } from "../../../api/auth";
import { sleep } from "../../../helpers";
import { AuthContext, AuthState } from "../AuthContext";

const RECOVERY_CODE_VALIDATION_SCHEMA = Joi.object({
  recoveryCode: Joi.string().length(36).required().messages({
    "string.empty": "Please enter a recovery code",
    "string.length": "Recovery code must be exactly 36 characters",
  }),
});

type RecoveryFields = {
  recoveryCode: string;
};

export const MFARecovery: FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<"success" | "error" | "neutral">(
    "neutral",
  );

  const { cognitoUser, setAuthState, setCognitoUser } = useContext(AuthContext);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RecoveryFields>({
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: joiResolver(RECOVERY_CODE_VALIDATION_SCHEMA),
  });

  // reset the auth state and clear the user
  const handleCancel = () => {
    // go back to login
    setAuthState(AuthState.LOGIN);

    // reset the cognito user
    setCognitoUser(undefined);
  };

  const onSubmit = async (data: RecoveryFields) => {
    setIsSubmitting(true);

    // wait for the loading state to be reflected
    await sleep(500);

    try {
      // attempt to verify the recovery code with cognito
      await UserActions.verifyRecoveryCode(
        cognitoUser?.username || "",
        data.recoveryCode,
        window.env.COGNITO_USER_POOL_ID,
      );

      toastSuccess("MFA has been disabled");

      // highlight the success state on the input
      setStatus("success");

      // wait for the toast to finish before redirecting
      await sleep(1500);

      // send the user back to log in
      window.location.href = withAppBasePath("/");
    } catch (err) {
      const error = err as Error;
      toastError(
        error.message || "An error occurred during recovery code verification",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <Input
          type="text"
          label="Recovery Code"
          placeholder="Enter your 32 character recovery code"
          status={errors.recoveryCode ? "error" : status}
          errorMsg={errors.recoveryCode?.message}
          msgPadded
          {...register("recoveryCode")}
        />
        <input type="submit" className="hidden" />
      </form>
      <div className="flex flex-col gap-6">
        <div className="flex gap-2">
          <Button
            className="flex-1"
            variant="v2/stroke"
            type="button"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            variant="v2/filled"
            className="flex-1"
            loading={isSubmitting}
            disabled={isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            Continue
          </Button>
        </div>
        <div className="text-sm text-zinc-500">
          Don&apos;t have a recovery code?{" "}
          <a
            href="mailto:support@alpaca.markets"
            className="text-sm text-zinc-900 underline hover:underline"
          >
            Contact support
          </a>
        </div>
      </div>
    </>
  );
};
